import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatPhoneNumber } from "@/utils/phoneFormatter";

export interface MarketingExclusionTarget {
  name: string;
  phone: string;
  /** The customer's current state: true means the dialog offers the undo. */
  excluded: boolean;
}

/**
 * The one confirmation for "Don't send marketing texts" and its undo, shared by
 * the roster row menu and the contact detail sheet. The body says plainly what
 * stops and what keeps sending.
 */
export function MarketingExclusionDialog({
  target,
  onOpenChange,
  onConfirm,
}: {
  target: MarketingExclusionTarget | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (target: MarketingExclusionTarget) => void;
}) {
  const phone = target ? formatPhoneNumber(target.phone) : "";
  return (
    <AlertDialog open={!!target} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {target &&
          (target.excluded ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Allow marketing texts to {target.name} again?</AlertDialogTitle>
                <AlertDialogDescription>
                  Order Invite and campaigns can text {phone} again whenever they qualify, and their order confirmations
                  can include offers again. Order confirmations themselves were never affected and keep sending.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="h-11">Keep them excluded</AlertDialogCancel>
                <AlertDialogAction className="h-11" onClick={() => onConfirm(target)}>
                  Allow marketing texts
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Stop marketing texts to {target.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Order Invite texts, campaign texts, and offers added to order confirmations stop for {phone}. The order
                  confirmation itself still sends. Their text club status does not change, and you can undo this any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="h-11">Keep sending</AlertDialogCancel>
                <AlertDialogAction
                  className="h-11 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => onConfirm(target)}
                >
                  Don't send marketing texts
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ))}
      </AlertDialogContent>
    </AlertDialog>
  );
}
